import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { EmptyCard, InfoLine, MetricCard, MetricMini, Pill } from './ui'
import { buildTrainingLessonPanels, cleanTrainingContentPreview, formatTrainingModuleTitle, formatTrustActor, getTrainingContentBlockText, isExpired, matchesViewerAssignmentRole, stripTrainingTestSuffix, toDateInput } from './utils'
import type { TrainingLessonPanelAsset } from './utils'
import type { WorkspaceData } from './types'
import { getAssetSignedUrl } from '../../lib/services/training'

type ReviewQueueFilter = 'all' | 'module' | 'asset' | 'quiz' | 'recommendation' | 'competency'

/**
 * Defense-in-depth: only render a fallback asset link as a clickable href
 * when its protocol is http(s). A `javascript:`/`data:` URL (however it got
 * into storage_ref) is rendered as inert text instead of a clickable anchor.
 */
function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Governed media playback for a training asset (#1764 Phase 1). Idle until
 * the learner/reviewer opts in ("Load ... preview"), then resolves a
 * short-lived signed URL via the training-assets resolver and renders a real
 * <audio>/<video>/<img> element (or a plain link for non-media mime types).
 * Playback stays locked behind reviewer approval, matching the governed
 * access copy already shown alongside this control.
 */
function AssetMediaPlayer({ asset }: { asset: TrainingLessonPanelAsset }) {
  const [state, setState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle')
  const [url, setUrl] = useState<string | null>(null)
  const [mimeType, setMimeType] = useState(asset.mime_type)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const canPlay = asset.status === 'approved'

  const handleLoad = async () => {
    setState('loading')
    setErrorMessage(null)
    try {
      const signed = await getAssetSignedUrl(asset.asset_id)
      setUrl(signed.url)
      if (signed.mime_type) setMimeType(signed.mime_type)
      setState('loaded')
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load media')
      setState('error')
    }
  }

  if (!canPlay) {
    return (
      <p className="mt-2 rounded-xl border border-dashed border-slate-300 bg-[var(--training-surface-2)] px-3 py-2 text-xs text-slate-500">
        Media playback unlocks once this asset is reviewer-approved.
      </p>
    )
  }

  if (state === 'idle') {
    return (
      <button type="button" className="btn-ghost mt-2 px-4 py-2 text-xs" onClick={() => void handleLoad()}>
        Load {asset.kind} preview
      </button>
    )
  }

  if (state === 'loading') {
    return <p className="mt-2 text-xs text-slate-500">Requesting signed playback URL…</p>
  }

  if (state === 'error') {
    return (
      <div className="mt-2 space-y-2">
        <p className="text-xs font-semibold text-rose-600">{errorMessage || 'Unable to load media.'}</p>
        <button type="button" className="btn-ghost px-4 py-2 text-xs" onClick={() => void handleLoad()}>
          Retry
        </button>
      </div>
    )
  }

  if (!url) return null

  const handleMediaError = () => {
    setState('error')
    setErrorMessage('The signed URL could not be played back. It may have expired — retry to request a fresh one.')
  }

  if (asset.kind === 'audio') {
    return <audio controls className="mt-2 w-full" src={url} onError={handleMediaError} />
  }
  if (asset.kind === 'video') {
    return <video controls className="mt-2 w-full rounded-xl" src={url} onError={handleMediaError} />
  }
  if (mimeType.startsWith('image/')) {
    return <img src={url} alt={`${asset.kind} asset preview`} className="mt-2 w-full rounded-xl" onError={handleMediaError} />
  }
  if (!isHttpUrl(url)) {
    return (
      <p className="mt-2 text-xs font-semibold text-rose-600">
        This asset's resolved link is not a valid http(s) URL and cannot be opened.
      </p>
    )
  }
  return (
    <a className="mt-2 inline-block text-xs font-semibold text-[var(--brand-teal)] underline" href={url} target="_blank" rel="noreferrer">
      Open {asset.kind} asset
    </a>
  )
}

export function OverviewSection({ data }: { data: WorkspaceData }) {
  return (
    <div className="space-y-6">
      <section className="training-hero">
        <div>
          <p className="training-eyebrow">Learning Operations</p>
          <h1 className="mt-2 font-brand text-3xl font-bold text-[var(--brand-deep)]">Training command center</h1>
          <p className="mt-3 max-w-3xl text-sm text-slate-600">
            Replace the beta module page with a learning workspace that tracks baseline content, reviewer controls, evidence, and role-aware execution across organizational tiers.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <MetricCard label="Modules" value={data.adminOverview?.module_count ?? data.modules.length} tone="blue" />
          <MetricCard label="Assignments" value={data.dashboardMetrics.assignmentCount} tone="teal" />
          <MetricCard label="Completions" value={data.adminOverview?.completion_count ?? data.dashboardMetrics.completionCount} tone="gold" />
          <MetricCard label="Expiring 30d" value={data.adminOverview?.expiring_within_30d_count ?? data.dashboardMetrics.expiringSoon} tone="slate" />
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1.3fr_0.7fr]">
        <div className="training-panel p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="training-eyebrow">Execution Filters</p>
              <h2 className="section-title mt-1">Operational health</h2>
            </div>
            <button type="button" className="btn-ghost px-4 py-2 text-sm" onClick={() => void data.loadAllData()}>
              Refresh
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <select value={data.dashboardFacilityFilter} onChange={(event) => data.setDashboardFacilityFilter(event.target.value)} className="training-input">
              <option value="">All facilities</option>
              {data.dashboardFacilityOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
            <select value={data.dashboardRoleFilter} onChange={(event) => data.setDashboardRoleFilter(event.target.value)} className="training-input">
              <option value="">All roles</option>
              {data.dashboardRoleOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
            <MetricMini label="Completion rate" value={`${data.dashboardMetrics.completionRate}%`} />
            <MetricMini label="Overdue assignments" value={String(data.dashboardMetrics.overdueAssignments)} />
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {data.assignments.slice(0, 4).map((assignment) => (
              <div key={assignment.assignment_id} className="rounded-2xl border border-[color:var(--training-border)] bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Assignment</p>
                <p className="mt-2 font-semibold text-slate-900">{formatTrainingModuleTitle(assignment.education_modules)}</p>
                <p className="mt-1 text-sm text-slate-600">Due {assignment.due_date}</p>
              </div>
            ))}
            {data.assignments.length === 0 ? <EmptyCard message="No assignments created yet. Use the assignments route to seed training obligations." /> : null}
          </div>
        </div>

        <div className="space-y-4">
          <div className="training-panel p-5">
            <p className="training-eyebrow">Video Adoption</p>
            <h2 className="section-title mt-1">Asset performance</h2>
            <div className="mt-4 space-y-3">
              <MetricMini label="Starts" value={String(data.videoSummary?.started ?? 0)} />
              <MetricMini label="Completions" value={String(data.videoSummary?.completed ?? 0)} />
              <MetricMini label="Completion rate" value={`${data.videoSummary?.completion_rate_percent ?? 0}%`} />
            </div>
            {data.videoSummary?.zero_completion_explanation ? (
              <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
                {data.videoSummary.zero_completion_explanation}
              </p>
            ) : null}
          </div>
          <div className="training-panel p-5">
            <p className="training-eyebrow">Content Guardrails</p>
            <ul className="mt-3 space-y-3 text-sm text-slate-600">
              <li>Baseline regulatory core remains immutable in V1.</li>
              <li>Facility overlays are constrained to addenda, policy links, and local execution notes.</li>
              <li>Derivative assets attach to approved module versions, not as source-of-truth content.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  )
}

export function LibrarySection({ data }: { data: WorkspaceData }) {
  const [libraryActionPanel, setLibraryActionPanel] = useState<'closed' | 'authoring' | 'quiz' | 'recommendation'>('closed')
  const moduleDetail = data.selectedModuleDetail
  const moduleDetailTitle = formatTrainingModuleTitle(moduleDetail, 'Select a module')

  return (
    <div className="space-y-6">
      <section className="training-panel p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="training-eyebrow">Training Library</p>
            <h1 className="section-title mt-1">Inspect and select training modules</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Start with module review: search, select a module, inspect its regulatory anchors, assets, overlays, and trust-signal metadata. Authoring and AI support stay available as explicit secondary actions.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <button
              type="button"
              className={libraryActionPanel === 'authoring' ? 'btn-primary px-4 py-2 text-sm' : 'btn-ghost px-4 py-2 text-sm'}
              onClick={() => setLibraryActionPanel((current) => current === 'authoring' ? 'closed' : 'authoring')}
            >
              Create module
            </button>
            <button
              type="button"
              className={libraryActionPanel === 'quiz' ? 'btn-primary px-4 py-2 text-sm' : 'btn-ghost px-4 py-2 text-sm'}
              onClick={() => setLibraryActionPanel((current) => current === 'quiz' ? 'closed' : 'quiz')}
            >
              Generate quiz
            </button>
            <button
              type="button"
              className={libraryActionPanel === 'recommendation' ? 'btn-primary px-4 py-2 text-sm' : 'btn-ghost px-4 py-2 text-sm'}
              onClick={() => setLibraryActionPanel((current) => current === 'recommendation' ? 'closed' : 'recommendation')}
            >
              Generate recommendation
            </button>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <MetricMini label="Modules" value={String(data.modules.length)} />
          <MetricMini label="Visible results" value={String(data.filteredModules.length)} />
          <MetricMini label="Tracks represented" value={String(data.libraryTrackSummary.length)} />
          <MetricMini label="Selected module" value={moduleDetail?.module_code || (moduleDetail ? 'Loaded' : 'Pending')} />
        </div>
      </section>

      {libraryActionPanel === 'authoring' ? (
        <section className="training-panel p-5 space-y-4">
          <div>
            <p className="training-eyebrow">Authoring</p>
            <h2 className="section-title mt-1">Create baseline module draft</h2>
            <p className="mt-2 text-sm text-slate-600">Draft creation is a secondary workflow. Close this panel to return to library inspection.</p>
          </div>
          <input value={data.moduleTitle} onChange={(event) => data.setModuleTitle(event.target.value)} placeholder="Module title" className="training-input" />
          <div className="grid gap-3 md:grid-cols-2">
            <input value={data.moduleCode} onChange={(event) => data.setModuleCode(event.target.value)} placeholder="Module code" className="training-input" />
            <input type="number" value={data.moduleDurationMinutes} onChange={(event) => data.setModuleDurationMinutes(Math.max(1, Number(event.target.value) || 15))} placeholder="Duration minutes" className="training-input" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <input value={data.moduleDomainTags} onChange={(event) => data.setModuleDomainTags(event.target.value)} placeholder="Domain tags (comma-separated)" className="training-input" />
            <select value={data.moduleLearningDepth} onChange={(event) => data.setModuleLearningDepth(event.target.value as 'awareness' | 'knowledge_attestation' | 'skill_demonstration' | 'validated_competency')} className="training-input">
              <option value="awareness">Awareness</option>
              <option value="knowledge_attestation">Knowledge attestation</option>
              <option value="skill_demonstration">Skill demonstration</option>
              <option value="validated_competency">Validated competency</option>
            </select>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <input value={data.moduleCadenceTags} onChange={(event) => data.setModuleCadenceTags(event.target.value)} placeholder="Cadence tags (comma-separated)" className="training-input" />
            <input value={data.moduleRoleApplicability} onChange={(event) => data.setModuleRoleApplicability(event.target.value)} placeholder="Role applicability (comma-separated)" className="training-input" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <input value={data.moduleTrackIds} onChange={(event) => data.setModuleTrackIds(event.target.value)} placeholder="Track IDs (comma-separated)" className="training-input" />
            <input value={data.moduleRecommendationTypes} onChange={(event) => data.setModuleRecommendationTypes(event.target.value)} placeholder="Recommendation types (comma-separated)" className="training-input" />
          </div>
          <input value={data.modulePolicyRefs} onChange={(event) => data.setModulePolicyRefs(event.target.value)} placeholder="Policy refs (comma-separated)" className="training-input" />
          <div className="grid gap-3 md:grid-cols-3">
            <input value={data.moduleAuthorName} onChange={(event) => data.setModuleAuthorName(event.target.value)} placeholder="Author name" className="training-input" />
            <input value={data.moduleReviewerName} onChange={(event) => data.setModuleReviewerName(event.target.value)} placeholder="Reviewer name" className="training-input" />
            <input value={data.moduleApproverName} onChange={(event) => data.setModuleApproverName(event.target.value)} placeholder="Approver name" className="training-input" />
          </div>
          <textarea value={data.moduleClinicalAccuracyScope} onChange={(event) => data.setModuleClinicalAccuracyScope(event.target.value)} placeholder="Clinical accuracy scope" className="training-input min-h-[88px]" />
          <div className="grid gap-3 md:grid-cols-2">
            <input type="date" value={data.moduleLastReviewedAt} onChange={(event) => data.setModuleLastReviewedAt(event.target.value)} className="training-input" />
            <input type="date" value={data.moduleNextReviewDueAt} onChange={(event) => data.setModuleNextReviewDueAt(event.target.value)} className="training-input" />
          </div>
          <input value={data.moduleStandards} onChange={(event) => data.setModuleStandards(event.target.value)} placeholder="Mapped standard IDs (comma-separated)" className="training-input" />
          <div className="space-y-2">
            <p className="training-eyebrow">Structured Blocks</p>
            {data.moduleBlocks.map((block, index) => (
              <div key={`module-block-${index}`} className="flex items-start gap-2">
                <textarea value={block.text} onChange={(event) => data.updateModuleBlock(index, event.target.value)} placeholder={`Block ${index + 1} content`} className="training-input min-h-[96px]" />
                <button type="button" onClick={() => data.removeModuleBlock(index)} className="btn-ghost px-3 py-2 text-xs" disabled={data.moduleBlocks.length === 1}>
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-ghost px-4 py-2 text-sm" onClick={data.addModuleBlock}>Add block</button>
            <input type="number" value={data.moduleExpiryDays} onChange={(event) => data.setModuleExpiryDays(Math.max(1, Number(event.target.value) || 365))} className="training-input max-w-[160px]" />
            <button type="button" className="btn-primary px-4 py-2 text-sm" disabled={data.isSubmitting} onClick={() => void data.handleCreateModule()}>
              Save draft
            </button>
          </div>
        </section>
      ) : null}

      <section className="training-panel p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="training-eyebrow">Training Library</p>
              <h2 className="section-title mt-1">{moduleDetailTitle}</h2>
            </div>
            <select value={data.selectedModuleId} onChange={(event) => data.setSelectedModuleId(event.target.value)} className="training-input min-w-[240px]">
              <option value="">Select module</option>
              {data.moduleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          {moduleDetail ? (
            <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-4">
                <div className="rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-4">
                  <div className="flex flex-wrap gap-2">
                    {moduleDetail.module_code ? <Pill>{moduleDetail.module_code}</Pill> : null}
                    {moduleDetail.learning_depth ? <Pill tone="blue">{moduleDetail.learning_depth}</Pill> : null}
                    {moduleDetail.library_origin ? <Pill tone="gold">{moduleDetail.library_origin}</Pill> : null}
                    {moduleDetail.duration_minutes ? <Pill>{moduleDetail.duration_minutes} min</Pill> : null}
                  </div>
                  <p className="mt-3 text-sm text-slate-600">{moduleDetail.clinical_accuracy_scope || 'Clinical accuracy scope is not yet defined for this draft.'}</p>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <InfoLine label="Domains" value={moduleDetail.domain_tags?.join(', ') || 'Not set'} />
                    <InfoLine label="Cadence" value={moduleDetail.cadence_tags?.join(', ') || 'Not set'} />
                    <InfoLine label="Role applicability" value={moduleDetail.role_applicability?.join(', ') || 'Not set'} />
                    <InfoLine label="Tracks" value={moduleDetail.related_track_ids?.join(', ') || 'Not set'} />
                  </div>
                </div>

                <div className="rounded-2xl border border-[color:var(--training-border)] bg-white p-4">
                  <p className="training-eyebrow">Regulatory Anchors</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {moduleDetail.education_module_citations?.length
                      ? moduleDetail.education_module_citations.map((citation) => (
                        <span key={citation.module_citation_id} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                          {citation.citation} · {citation.label}
                        </span>
                      ))
                      : <span className="text-sm text-slate-500">No structured citations attached yet.</span>}
                  </div>
                  <div className="mt-4">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Policy references</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {moduleDetail.linked_policy_refs?.length
                        ? moduleDetail.linked_policy_refs.map((policyRef) => (
                          <span key={`${moduleDetail.module_id}-${policyRef}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                            {policyRef}
                          </span>
                        ))
                        : <span className="text-sm text-slate-500">No package policy references attached yet.</span>}
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Resolved policy links</p>
                    <div className="mt-2 space-y-2">
                      {moduleDetail.education_module_policy_links?.length
                        ? moduleDetail.education_module_policy_links.map((policyLink) => (
                          <div key={policyLink.module_policy_link_id} className="rounded-xl border border-slate-200 bg-[var(--training-surface-2)] px-3 py-2 text-sm text-slate-700">
                            <p className="font-semibold text-slate-900">{policyLink.policies?.title || policyLink.reference_label || policyLink.policy_id}</p>
                            <p className="mt-1 text-xs text-slate-500">
                              {[policyLink.policies?.category, policyLink.policies?.status, policyLink.policies?.version_number ? `v${policyLink.policies.version_number}` : null].filter(Boolean).join(' · ') || 'Linked policy'}
                            </p>
                          </div>
                        ))
                        : <span className="text-sm text-slate-500">No normalized policy links resolved yet.</span>}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-[color:var(--training-border)] bg-white p-4">
                  <p className="training-eyebrow">Module Source Preview</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {moduleDetail.mapped_standard_ids?.length
                      ? moduleDetail.mapped_standard_ids.map((standardId: string) => (
                        <Pill key={`${moduleDetail.module_id}-${standardId}`} tone="blue">{standardId}</Pill>
                      ))
                      : <span className="text-sm text-slate-500">No linked standards attached yet.</span>}
                  </div>
                  <div className="mt-4 space-y-3">
                    {moduleDetail.content_blocks?.length
                      ? moduleDetail.content_blocks.slice(0, 5).map((block, index) => (
                        <div key={`${moduleDetail.module_id}-block-${index}`} className="rounded-xl border border-slate-200 bg-[var(--training-surface-2)] p-3 text-sm text-slate-700">
                          {cleanTrainingContentPreview(getTrainingContentBlockText(block)) || 'Empty block'}
                        </div>
                      ))
                      : <span className="text-sm text-slate-500">No structured source blocks available.</span>}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-[color:var(--training-border)] bg-white p-4">
                  <p className="training-eyebrow">Asset Manifests</p>
                  <div className="mt-3 space-y-2">
                    {moduleDetail.education_module_assets?.length
                      ? moduleDetail.education_module_assets.map((asset) => (
                        <div key={asset.asset_id} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-semibold capitalize text-slate-700">{asset.kind}</span>
                            <Pill>{asset.status}</Pill>
                          </div>
                          <p className="mt-1 text-xs text-slate-500">{asset.storage_mode} · {asset.mime_type}</p>
                          <AssetMediaPlayer
                            asset={{
                              asset_id: asset.asset_id,
                              kind: asset.kind,
                              status: asset.status,
                              storage_mode: asset.storage_mode,
                              mime_type: asset.mime_type,
                            }}
                          />
                        </div>
                      ))
                      : <p className="text-sm text-slate-500">No derivative assets attached yet.</p>}
                  </div>
                </div>

                <div className="rounded-2xl border border-[color:var(--training-border)] bg-white p-4">
                  <p className="training-eyebrow">Overlay And Review Readiness</p>
                  <InfoLine label="Overlays" value={String(moduleDetail.education_module_overlays?.length || 0)} />
                  <InfoLine label="Last reviewed" value={moduleDetail.last_reviewed_at ? new Date(moduleDetail.last_reviewed_at).toLocaleDateString() : 'Pending'} />
                  <InfoLine label="Next review due" value={moduleDetail.next_review_due_at ? new Date(moduleDetail.next_review_due_at).toLocaleDateString() : 'Pending'} />
                  <InfoLine label="Recommendation types" value={moduleDetail.related_recommendation_types?.join(', ') || 'Not set'} />
                  <div className="mt-4 grid gap-3">
                    <InfoLine label="Author" value={formatTrustActor(moduleDetail.trust_signal_actors?.author)} />
                    <InfoLine label="Reviewer" value={formatTrustActor(moduleDetail.trust_signal_actors?.reviewer)} />
                    <InfoLine label="Approver" value={formatTrustActor(moduleDetail.trust_signal_actors?.approver)} />
                  </div>
                  <p className="mt-3 text-xs text-slate-500">V1 overlays are constrained addenda only. Baseline regulatory core remains immutable.</p>
                </div>
              </div>
            </div>
          ) : (
            <EmptyCard message="Choose a module to inspect citations, assets, and trust-signal metadata." />
          )}
      </section>

      {libraryActionPanel === 'quiz' ? (
        <section className="training-panel p-5 space-y-4">
          <div>
            <p className="training-eyebrow">AI Support</p>
            <h2 className="section-title mt-1">Quiz draft generation</h2>
            <p className="mt-2 text-sm text-slate-600">Generate a reviewer-queued quiz from the currently selected module.</p>
          </div>
          <select value={data.selectedModuleId} onChange={(event) => data.setSelectedModuleId(event.target.value)} className="training-input">
            <option value="">Select module</option>
            {data.moduleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <textarea value={data.quizPrompt} onChange={(event) => data.setQuizPrompt(event.target.value)} className="training-input min-h-[112px]" />
          <button type="button" className="btn-primary px-4 py-2 text-sm" disabled={data.isSubmitting || !data.selectedModuleId} onClick={() => void data.handleGenerateQuizDraft()}>
            Generate draft quiz
          </button>
        </section>
      ) : null}

      {libraryActionPanel === 'recommendation' ? (
        <section className="training-panel p-5 space-y-4">
          <div>
            <p className="training-eyebrow">Recommendation Loop</p>
            <h2 className="section-title mt-1">Recommendation drafts</h2>
            <p className="mt-2 text-sm text-slate-600">Generate recommendation drafts for reviewer approval without making the Library default view an authoring surface.</p>
          </div>
          <input value={data.recommendationStandards} onChange={(event) => data.setRecommendationStandards(event.target.value)} placeholder="Standard IDs (comma-separated)" className="training-input" />
          <textarea value={data.recommendationPrompt} onChange={(event) => data.setRecommendationPrompt(event.target.value)} className="training-input min-h-[112px]" />
          <button type="button" className="btn-ghost px-4 py-2 text-sm" disabled={data.isSubmitting || !data.selectedModuleId} onClick={() => void data.handleGenerateRecommendationDraft()}>
            Generate recommendation draft
          </button>
        </section>
      ) : null}

      <section className="training-panel p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="training-eyebrow">Library Filters</p>
            <h2 className="section-title mt-1">Search training modules</h2>
          </div>
          <MetricMini label="Results" value={String(data.filteredModules.length)} />
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <input value={data.librarySearch} onChange={(event) => data.setLibrarySearch(event.target.value)} placeholder="Search title, code, standard, track" className="training-input" />
          <select value={data.libraryStatusFilter} onChange={(event) => data.setLibraryStatusFilter(event.target.value as '' | 'draft' | 'approved' | 'retired')} className="training-input">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="approved">Approved</option>
            <option value="retired">Retired</option>
          </select>
          <select value={data.libraryDepthFilter} onChange={(event) => data.setLibraryDepthFilter(event.target.value as '' | 'awareness' | 'knowledge_attestation' | 'skill_demonstration' | 'validated_competency')} className="training-input">
            <option value="">All depths</option>
            <option value="awareness">Awareness</option>
            <option value="knowledge_attestation">Knowledge attestation</option>
            <option value="skill_demonstration">Skill demonstration</option>
            <option value="validated_competency">Validated competency</option>
          </select>
          <select value={data.libraryDomainFilter} onChange={(event) => data.setLibraryDomainFilter(event.target.value)} className="training-input">
            <option value="">All domains</option>
            {data.libraryDomainOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          <select value={data.libraryTrackFilter} onChange={(event) => data.setLibraryTrackFilter(event.target.value)} className="training-input">
            <option value="">All tracks</option>
            {data.libraryTrackOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        {data.libraryTrackSummary.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {data.libraryTrackSummary.slice(0, 8).map((track) => (
              <div key={track.trackId} className="rounded-2xl border border-[color:var(--training-border)] bg-white px-4 py-3">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Track</p>
                <p className="mt-1 font-semibold text-slate-900">{track.trackId}</p>
                <p className="mt-1 text-sm text-slate-600">{track.moduleCount} module{track.moduleCount === 1 ? '' : 's'}</p>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="training-panel overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto_auto] border-b border-[color:var(--training-border)] bg-[var(--training-surface-2)] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
          <span>Module</span>
          <span>Version</span>
          <span>Status</span>
          <span>Expiry</span>
        </div>
        {data.filteredModules.map((moduleRow) => (
          <button
            key={moduleRow.module_id}
            type="button"
            onClick={() => data.setSelectedModuleId(moduleRow.module_id)}
            className="grid w-full grid-cols-[1fr_auto_auto_auto] gap-2 border-b border-slate-100 px-4 py-3 text-left text-sm transition hover:bg-[var(--training-surface-2)]"
          >
            <div>
              <span className="font-semibold text-slate-700">{formatTrainingModuleTitle(moduleRow)}</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {moduleRow.module_code ? <Pill tone="gold">{moduleRow.module_code}</Pill> : null}
                {moduleRow.domain_tags?.slice(0, 3).map((tag) => <Pill key={`${moduleRow.module_id}-${tag}`}>{tag}</Pill>)}
                {moduleRow.duration_minutes ? <Pill tone="blue">{moduleRow.duration_minutes} min</Pill> : null}
                {moduleRow.related_track_ids?.slice(0, 1).map((trackId) => <Pill key={`${moduleRow.module_id}-${trackId}`} tone="rose">{trackId}</Pill>)}
              </div>
            </div>
            <span className="text-slate-600">v{moduleRow.version_number}</span>
            <span className={moduleRow.status === 'approved' ? 'badge-released' : moduleRow.status === 'retired' ? 'badge-archived' : 'badge-in-progress'}>{moduleRow.status}</span>
            <span className="text-slate-600">{moduleRow.expiration_interval_days}d</span>
          </button>
        ))}
        {data.filteredModules.length === 0 ? <div className="px-4 py-4 text-sm text-slate-500">No modules match the current library filters.</div> : null}
      </section>
    </div>
  )
}

export function TracksSection({ data }: { data: WorkspaceData }) {
  const selectedTrack = data.selectedTrackDetail
  const groupedModules = {
    approved: (selectedTrack?.modules || []).filter((moduleRow) => moduleRow.status === 'approved'),
    draft: (selectedTrack?.modules || []).filter((moduleRow) => moduleRow.status === 'draft'),
    retired: (selectedTrack?.modules || []).filter((moduleRow) => moduleRow.status === 'retired'),
  }

  return (
    <div className="space-y-6">
      <section className="training-panel p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="training-eyebrow">Track Registry</p>
            <h1 className="section-title mt-1">Learning tracks</h1>
            <p className="mt-2 text-sm text-slate-600">Track titles now resolve against first-class training track records, and admins can deploy full tracks directly into assignment workflows from this workspace.</p>
          </div>
          <select value={data.selectedTrackId} onChange={(event) => data.setSelectedTrackId(event.target.value)} className="training-input min-w-[260px]">
            <option value="">Select track</option>
            {data.tracks.map((track) => <option key={track.track_id} value={track.track_id}>{track.title}</option>)}
          </select>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.tracks.map((track) => (
          <button key={track.track_id} type="button" className="training-panel p-5 text-left transition hover:-translate-y-0.5 hover:shadow-lg" onClick={() => data.setSelectedTrackId(track.track_id)}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="training-eyebrow">{track.track_key}</p>
                <h2 className="section-title mt-1">{track.title}</h2>
              </div>
              <Pill tone={track.status === 'active' ? 'blue' : 'rose'}>{track.status}</Pill>
            </div>
            <p className="mt-3 text-sm text-slate-600">{track.description || 'No description yet. Package-imported modules can still join this track.'}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <MetricMini label="Modules" value={String(track.module_count)} />
              <MetricMini label="Deployments" value={String(track.deployment_count ?? 0)} />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              {track.last_deployed_at ? `Last deployed ${new Date(track.last_deployed_at).toLocaleDateString()}` : 'No deployments yet'}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {data.filteredModules
                .filter((moduleRow) => moduleRow.related_track_ids?.includes(track.track_key))
                .slice(0, 4)
                .map((moduleRow) => (
                  <span key={`${track.track_id}-${moduleRow.module_id}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                    {moduleRow.module_code || formatTrainingModuleTitle(moduleRow)}
                  </span>
                ))}
            </div>
          </button>
        ))}
        {data.tracks.length === 0 ? (
          <div className="training-panel border border-dashed border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-6 md:col-span-2 xl:col-span-3">
            <p className="training-eyebrow">No Tracks Registered</p>
            <h2 className="section-title mt-1">Build a track before deployment</h2>
            <p className="mt-3 max-w-3xl text-sm text-slate-600">
              Tracks bundle approved modules into role-ready training plans so admins can deploy a complete requirement set with one due date, scope, and audit trail. They are populated by imported module packages or by module drafts that carry related track IDs.
            </p>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              <MetricMini label="1. Import or author" value="Add modules" />
              <MetricMini label="2. Tag membership" value="Set track IDs" />
              <MetricMini label="3. Return here" value="Deploy track" />
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a href="/training/library" className="btn-primary px-4 py-2 text-sm">Open Library authoring</a>
              <span className="text-sm text-slate-600">Use the Library module form or package import workflow to add related track IDs, then return to Tracks to assign the full bundle.</span>
            </div>
          </div>
        ) : null}
      </section>

      {selectedTrack ? (
        <section className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-4">
            <div className="training-panel p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="training-eyebrow">{selectedTrack.track_key}</p>
                  <h2 className="section-title mt-1">{selectedTrack.title}</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Pill tone={selectedTrack.status === 'active' ? 'blue' : 'rose'}>{selectedTrack.status}</Pill>
                  <Pill>{selectedTrack.module_count} modules</Pill>
                  <Pill tone="gold">{selectedTrack.deployment_count ?? selectedTrack.deployments.length} deployments</Pill>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-600">{selectedTrack.description || 'No track description yet.'}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <MetricMini label="Approved" value={String(groupedModules.approved.length)} />
                <MetricMini label="Draft" value={String(groupedModules.draft.length)} />
                <MetricMini label="Last deployed" value={selectedTrack.last_deployed_at ? new Date(selectedTrack.last_deployed_at).toLocaleDateString() : 'Pending'} />
              </div>
            </div>

            <div className="training-panel p-5 space-y-4">
              <div>
                <p className="training-eyebrow">Track Modules</p>
                <h3 className="section-title mt-1">Track membership</h3>
              </div>
              {(['approved', 'draft', 'retired'] as const).map((statusKey) => (
                <div key={statusKey} className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">{statusKey}</p>
                    <span className="text-xs text-slate-500">{groupedModules[statusKey].length}</span>
                  </div>
                  {groupedModules[statusKey].length > 0 ? groupedModules[statusKey].map((moduleRow) => (
                    <button key={moduleRow.module_id} type="button" className="w-full rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-4 text-left" onClick={() => data.setSelectedModuleId(moduleRow.module_id)}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-900">{formatTrainingModuleTitle(moduleRow)}</span>
                        {moduleRow.module_code ? <Pill tone="gold">{moduleRow.module_code}</Pill> : null}
                        {moduleRow.learning_depth ? <Pill tone="blue">{moduleRow.learning_depth}</Pill> : null}
                        {moduleRow.duration_minutes ? <Pill>{moduleRow.duration_minutes} min</Pill> : null}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(moduleRow.domain_tags || []).slice(0, 4).map((tag) => <Pill key={`${moduleRow.module_id}-${tag}`}>{tag}</Pill>)}
                      </div>
                    </button>
                  )) : <EmptyCard message={`No ${statusKey} modules in this track.`} />}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="training-panel p-5 space-y-4">
              <div>
                <p className="training-eyebrow">Deployment Launch</p>
                <h3 className="section-title mt-1">Assign this track</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <select value={data.trackDeploymentScope} onChange={(event) => data.setTrackDeploymentScope(event.target.value as 'facility' | 'role' | 'individual')} className="training-input">
                  <option value="facility">Facility</option>
                  <option value="role">Role</option>
                  <option value="individual">Individual</option>
                </select>
                <input type="date" value={data.trackDeploymentDueDate} onChange={(event) => data.setTrackDeploymentDueDate(event.target.value)} className="training-input" />
              </div>
              {data.trackDeploymentScope === 'facility' ? (
                <input value={data.trackDeploymentFacilityId} onChange={(event) => data.setTrackDeploymentFacilityId(event.target.value)} placeholder="Facility id" className="training-input" />
              ) : null}
              {data.trackDeploymentScope === 'role' ? (
                <input value={data.trackDeploymentTargetRole} onChange={(event) => data.setTrackDeploymentTargetRole(event.target.value)} placeholder="Target role" className="training-input" />
              ) : null}
              {data.trackDeploymentScope === 'individual' ? (
                <input value={data.trackDeploymentAssignee} onChange={(event) => data.setTrackDeploymentAssignee(event.target.value)} placeholder="Assignee user id" className="training-input" />
              ) : null}
              <textarea value={data.trackDeploymentNotes} onChange={(event) => data.setTrackDeploymentNotes(event.target.value)} placeholder="Deployment notes" className="training-input min-h-[96px]" />
              <button type="button" className="btn-primary px-4 py-2 text-sm" disabled={data.isSubmitting} onClick={() => void data.handleCreateTrackDeployment()}>
                Deploy track assignments
              </button>
            </div>

            <div className="training-panel p-5 space-y-4">
              <div>
                <p className="training-eyebrow">Deployment History</p>
                <h3 className="section-title mt-1">Track rollout ledger</h3>
              </div>
              {selectedTrack.deployments.length > 0 ? selectedTrack.deployments.map((deployment) => (
                <div key={deployment.deployment_id} className="rounded-2xl border border-[color:var(--training-border)] bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Pill tone={deployment.deployment_status === 'completed' ? 'blue' : deployment.deployment_status === 'failed' ? 'rose' : 'gold'}>{deployment.deployment_status}</Pill>
                      <Pill>{deployment.deployment_scope}</Pill>
                      <Pill tone="gold">{deployment.assignment_count} assignments</Pill>
                    </div>
                    <span className="text-xs text-slate-500">{new Date(deployment.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-3 grid gap-2 text-sm text-slate-600">
                    <InfoLine label="Due date" value={deployment.due_date} />
                    <InfoLine label="Target role" value={deployment.target_role || 'Not role-scoped'} />
                    <InfoLine label="Facility" value={deployment.facility_id || 'Not facility-scoped'} />
                  </div>
                  {deployment.deployment_notes ? <p className="mt-3 text-sm text-slate-600">{deployment.deployment_notes}</p> : null}
                  {deployment.assignments?.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {deployment.assignments.slice(0, 6).map((assignment) => (
                        <span key={assignment.assignment_id} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                          {assignment.education_modules?.module_code || formatTrainingModuleTitle(assignment.education_modules, assignment.module_id)}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              )) : <EmptyCard message="No deployments have been recorded for this track yet." />}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  )
}

export function AssignmentsSection({ data }: { data: WorkspaceData }) {
  const [assignmentReferenceTime] = useState(() => Date.now())
  const competencyValidationByAssignment = new Map(
    data.competencyValidations.map((validation) => [validation.assignment_id, validation]),
  )

  return (
    <div className="space-y-6">
      <section className="training-panel p-5 space-y-4">
        <div>
          <p className="training-eyebrow">Assignment Engine</p>
          <h1 className="section-title mt-1">Orchestrate training obligations</h1>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <select aria-label="Assignment module" value={data.assignmentModuleId} onChange={(event) => data.setAssignmentModuleId(event.target.value)} className="training-input">
            <option value="">Module</option>
            {data.moduleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <select aria-label="Assignment scope" value={data.assignmentScope} onChange={(event) => data.setAssignmentScope(event.target.value as 'facility' | 'role' | 'individual')} className="training-input">
            <option value="facility">Facility</option>
            <option value="role">Role</option>
            <option value="individual">Individual</option>
          </select>
          <input type="date" value={data.assignmentDueDate} onChange={(event) => data.setAssignmentDueDate(event.target.value)} className="training-input" />
          {data.assignmentScope === 'role' ? (
            <input value={data.assignmentTargetRole} onChange={(event) => data.setAssignmentTargetRole(event.target.value)} placeholder="Target role" className="training-input" />
          ) : data.assignmentScope === 'individual' ? (
            <div>
              <input
                value={data.assignmentAssignee}
                onChange={(event) => data.setAssignmentAssignee(event.target.value)}
                placeholder="Search learner email or paste user id"
                className="training-input"
                list="training-assignee-options"
              />
              <datalist id="training-assignee-options">
                {data.assignmentUserOptions.map((user) => (
                  <option key={user.user_id} value={user.email}>
                    {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email} ({user.role})
                  </option>
                ))}
              </datalist>
              <p className="mt-1 text-xs text-slate-500">Choose a learner from email search; the assignment stores the user id.</p>
            </div>
          ) : (
            <input disabled readOnly value="" placeholder="Facility resolver handled backend-side" className="training-input bg-slate-50 text-slate-400" />
          )}
        </div>
        <button type="button" className="btn-primary px-4 py-2 text-sm" disabled={data.isSubmitting} onClick={() => void data.handleCreateAssignment()}>
          Create assignment
        </button>
      </section>

      <section className="grid gap-3">
        {data.assignments.map((assignment) => {
          const dueAt = new Date(`${assignment.due_date}T23:59:59.999Z`).getTime()
          const daysUntilDue = Math.ceil((dueAt - assignmentReferenceTime) / 86400000)
          const sourceLabel = assignment.assignment_scope === 'facility' ? 'Facility assignment' : assignment.assignment_scope === 'role' ? 'Role based' : 'Individual'
          const learningDepth = assignment.education_modules?.learning_depth || null
          const validation = competencyValidationByAssignment.get(assignment.assignment_id)
          return (
            <div key={assignment.assignment_id} className="training-panel p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900">{formatTrainingModuleTitle(assignment.education_modules, 'Module')}</span>
                  <span className="text-xs font-semibold text-slate-500">{sourceLabel}</span>
                  {learningDepth === 'skill_demonstration' || learningDepth === 'validated_competency' ? <Pill tone="gold">{learningDepth}</Pill> : null}
                  {validation ? <Pill tone={validation.validation_status === 'approved' ? 'blue' : validation.validation_status === 'rejected' ? 'rose' : 'gold'}>{validation.validation_status}</Pill> : null}
                  {assignment.linked_capa_id ? <Pill tone="gold">CAPA linked</Pill> : null}
                  {daysUntilDue <= 30 && daysUntilDue > 0 ? <Pill tone="gold">Due in {daysUntilDue}d</Pill> : null}
                  {daysUntilDue <= 0 ? <Pill tone="rose">Overdue</Pill> : null}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Due {assignment.due_date}</span>
                  <span className="text-xs font-semibold text-slate-500">Learners complete this from My Learning after attestation.</span>
                </div>
              </div>
            </div>
          )
        })}
        {data.assignments.length === 0 ? <EmptyCard message="No assignments yet. This workspace will populate as recommendations are approved and obligations are issued." /> : null}
      </section>
    </div>
  )
}

export function ReviewSection({ data }: { data: WorkspaceData }) {
  const [defaultRecommendationDueDate] = useState(() =>
    toDateInput(new Date(Date.now() + 14 * 86400000).toISOString()),
  )
  const [reviewQueueFilter, setReviewQueueFilter] = useState<ReviewQueueFilter>('all')
  const pendingCompetencyValidations = data.competencyValidations.filter((validation) => validation.validation_status === 'pending')
  const pendingRecommendations = data.recommendations.filter((recommendation) => recommendation.status === 'draft')
  const reviewerQueueCounts = data.reviewQueue.reduce<Record<'module' | 'asset' | 'quiz', number>>(
    (counts, item) => {
      counts[item.entity_type] += 1
      return counts
    },
    { module: 0, asset: 0, quiz: 0 },
  )
  const totalPendingReviewItems = data.reviewQueue.length + pendingRecommendations.length + pendingCompetencyValidations.length
  const visibleReviewQueue = data.reviewQueue
    .filter((item) => reviewQueueFilter === 'all' || item.entity_type === reviewQueueFilter)
    .sort((first, second) => new Date(first.created_at).getTime() - new Date(second.created_at).getTime())
  const visibleRecommendations = (reviewQueueFilter === 'all' || reviewQueueFilter === 'recommendation'
    ? pendingRecommendations
    : []
  ).sort((first, second) => new Date(first.created_at).getTime() - new Date(second.created_at).getTime())
  const visibleCompetencyValidations = (reviewQueueFilter === 'all' || reviewQueueFilter === 'competency'
    ? pendingCompetencyValidations
    : []
  ).sort((first, second) => new Date(first.created_at).getTime() - new Date(second.created_at).getTime())
  const oldestReviewItem = [
    ...data.reviewQueue.map((item) => ({
      type: item.entity_type,
      title: stripTrainingTestSuffix(item.title) || stripTrainingTestSuffix(item.subtitle) || item.module_id,
      createdAt: item.created_at,
    })),
    ...pendingRecommendations.map((recommendation) => ({
      type: 'recommendation',
      title: recommendation.recommendation_text,
      createdAt: recommendation.created_at,
    })),
    ...pendingCompetencyValidations.map((validation) => ({
      type: 'competency',
      title: formatTrainingModuleTitle(validation.training_assignments?.education_modules, validation.assignment_id),
      createdAt: validation.created_at,
    })),
  ].sort((first, second) => new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime())[0]
  const reviewFilters: Array<{ id: ReviewQueueFilter; label: string; count: number }> = [
    { id: 'all', label: 'All', count: totalPendingReviewItems },
    { id: 'module', label: 'Modules', count: reviewerQueueCounts.module },
    { id: 'asset', label: 'Assets', count: reviewerQueueCounts.asset },
    { id: 'quiz', label: 'Quizzes', count: reviewerQueueCounts.quiz },
    { id: 'recommendation', label: 'Recommendations', count: pendingRecommendations.length },
    { id: 'competency', label: 'Competencies', count: pendingCompetencyValidations.length },
  ]

  return (
    <div className="space-y-6">
      <section className="training-panel p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="training-eyebrow">Review Worklist</p>
            <h1 className="section-title mt-1">Pending review summary</h1>
            <p className="mt-2 text-sm text-slate-600">
              Filter the queue by item type and use oldest-item context before opening full review cards.
            </p>
          </div>
          <Pill tone={totalPendingReviewItems > 0 ? 'gold' : 'blue'}>{totalPendingReviewItems} pending</Pill>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <MetricMini label="Modules" value={String(reviewerQueueCounts.module)} />
          <MetricMini label="Assets" value={String(reviewerQueueCounts.asset)} />
          <MetricMini label="Quizzes" value={String(reviewerQueueCounts.quiz)} />
          <MetricMini label="Recommendations" value={String(pendingRecommendations.length)} />
          <MetricMini label="Competencies" value={String(pendingCompetencyValidations.length)} />
          <MetricMini label="Total pending" value={String(totalPendingReviewItems)} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {reviewFilters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={reviewQueueFilter === filter.id ? 'btn-primary px-3 py-2 text-xs' : 'btn-ghost px-3 py-2 text-xs'}
              onClick={() => setReviewQueueFilter(filter.id)}
            >
              {filter.label} {filter.count}
            </button>
          ))}
        </div>
        <div className="mt-4 rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-3 text-sm text-slate-700">
          <p className="training-eyebrow">Oldest awaiting action</p>
          {oldestReviewItem ? (
            <p className="mt-2">
              <span className="font-semibold capitalize text-slate-900">{oldestReviewItem.type}</span>
              <span className="mx-2 text-slate-400">|</span>
              <span>{oldestReviewItem.title}</span>
              <span className="mx-2 text-slate-400">|</span>
              <span>{new Date(oldestReviewItem.createdAt).toLocaleDateString()}</span>
            </p>
          ) : (
            <p className="mt-2 text-slate-500">No pending review work.</p>
          )}
        </div>
      </section>

      <section className="training-panel overflow-hidden">
        <div className="border-b border-[color:var(--training-border)] bg-[var(--training-surface-2)] px-4 py-3">
          <p className="training-eyebrow">Reviewer Queue</p>
          <h1 className="section-title mt-1">Review workflow</h1>
        </div>
        {visibleReviewQueue.length === 0 ? <div className="px-4 py-4 text-sm text-slate-500">No reviewer items awaiting action for this filter.</div> : null}
        {visibleReviewQueue.map((item) => {
          const moduleDetail = item.detail.module
          const assetDetail = item.detail.asset
          const quizDetail = item.detail.quiz
          return (
          <div key={item.queue_key} className="border-b border-slate-100 px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-slate-900">{stripTrainingTestSuffix(item.title) || (assetDetail ? `${assetDetail.kind} asset` : item.entity_type)}</p>
                  <Pill tone={item.entity_type === 'module' ? 'gold' : item.entity_type === 'asset' ? 'blue' : 'slate'}>{item.entity_type}</Pill>
                </div>
                <p className="mt-1 text-sm text-slate-600">{stripTrainingTestSuffix(item.subtitle) || item.module_id}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="badge-in-progress">{item.status}</span>
                <button type="button" className="btn-primary px-3 py-2 text-xs" disabled={data.isSubmitting} onClick={() => void data.handleApproveReviewItem(item.entity_type, item.entity_id, item.queue_key)}>Approve</button>
                <button type="button" className="rounded-xl border border-rose-300 px-3 py-2 text-xs font-bold text-rose-700" disabled={data.isSubmitting} onClick={() => void data.handleRejectReviewItem(item.entity_type, item.entity_id, item.queue_key)}>Reject</button>
              </div>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <div className="rounded-2xl border border-[color:var(--training-border)] bg-white p-3 text-sm text-slate-700">
                {moduleDetail ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Pill>{moduleDetail.module_code || moduleDetail.module_id}</Pill>
                      {moduleDetail.learning_depth ? <Pill tone="blue">{moduleDetail.learning_depth}</Pill> : null}
                      {moduleDetail.duration_minutes ? <Pill>{moduleDetail.duration_minutes} min</Pill> : null}
                    </div>
                    <p>{moduleDetail.clinical_accuracy_scope || 'Clinical accuracy scope pending.'}</p>
                    <p className="text-xs text-slate-500">
                      Standards: {moduleDetail.mapped_standard_ids?.join(', ') || 'Not set'} · Assets: {moduleDetail.education_module_assets?.length ?? 0}
                    </p>
                  </div>
                ) : null}
                {assetDetail ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Pill>{assetDetail.kind}</Pill>
                      <Pill tone="blue">{assetDetail.asset_version}</Pill>
                    </div>
                    {item.title ? null : <p>{`${assetDetail.kind} asset`}</p>}
                    <p className="break-all text-xs text-slate-500">{assetDetail.storage_ref}</p>
                    <p className="text-xs text-slate-500">
                      {assetDetail.education_modules?.module_code || item.module_id} · {assetDetail.mime_type}
                    </p>
                  </div>
                ) : null}
                {quizDetail ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Pill>{quizDetail.ai_generated ? 'AI draft' : 'Manual draft'}</Pill>
                      <Pill tone="blue">{quizDetail.passing_score_percent || 80}% pass</Pill>
                    </div>
                    <p className="text-xs text-slate-500">
                      {quizDetail.education_modules?.module_code || quizDetail.module_id} · {formatTrainingModuleTitle(quizDetail.education_modules)}
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-3">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Governance trail</p>
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  {item.decision_history?.length
                    ? item.decision_history.slice(0, 3).map((decision) => (
                      <div key={decision.review_decision_id} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <p className="font-semibold capitalize text-slate-900">{decision.decision}</p>
                        <p className="mt-1 text-xs text-slate-500">{new Date(decision.created_at).toLocaleString()}</p>
                        {decision.review_comment ? <p className="mt-2 text-sm text-slate-600">{decision.review_comment}</p> : null}
                      </div>
                    ))
                    : <p className="text-sm text-slate-500">No decisions recorded yet.</p>}
                </div>
              </div>
            </div>
            <textarea value={data.reviewDecisionComments[item.queue_key] || ''} onChange={(event) => data.setReviewDecisionComment(item.queue_key, event.target.value)} placeholder="Reviewer rationale/comment (required to reject)" className="training-input mt-3 min-h-[88px]" />
            <p className="mt-2 text-xs text-slate-500">{data.reviewDecisionAction[item.queue_key] ? `Last action: ${data.reviewDecisionAction[item.queue_key]}` : 'Awaiting reviewer action'}</p>
          </div>
        )})}
      </section>

      <section className="training-panel overflow-hidden">
        <div className="border-b border-[color:var(--training-border)] bg-[var(--training-surface-2)] px-4 py-3">
          <p className="training-eyebrow">Recommendations</p>
          <h2 className="section-title mt-1">Approve remediation signals</h2>
        </div>
        {visibleRecommendations.length === 0 ? <div className="px-4 py-4 text-sm text-slate-500">No recommendation drafts pending review for this filter.</div> : null}
        {visibleRecommendations.map((recommendation) => (
          <div key={recommendation.recommendation_id} className="border-b border-slate-100 px-4 py-4 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-slate-900">{recommendation.recommendation_text}</p>
                <p className="mt-1 text-slate-600">{recommendation.standard_id}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge-in-progress">{recommendation.status}</span>
                <button type="button" className="btn-ghost px-3 py-2 text-xs" disabled={data.isSubmitting} onClick={() => void data.handleApproveRecommendation(recommendation.recommendation_id)}>
                  Approve only
                </button>
              </div>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-[1fr_180px_auto]">
              <select
                value={data.recommendationRemediationStrengths[recommendation.recommendation_id] || 'training_only'}
                onChange={(event) => data.setRecommendationRemediationStrength(recommendation.recommendation_id, event.target.value as 'training_only' | 'training_plus_checklist' | 'training_plus_system_change')}
                className="training-input"
              >
                <option value="training_only">Training only</option>
                <option value="training_plus_checklist">Training + checklist</option>
                <option value="training_plus_system_change">Training + system change</option>
              </select>
              <input
                type="date"
                value={data.recommendationDueDates[recommendation.recommendation_id] || defaultRecommendationDueDate}
                onChange={(event) => data.setRecommendationDueDate(recommendation.recommendation_id, event.target.value)}
                className="training-input"
              />
              <button
                type="button"
                className="btn-primary px-4 py-2 text-xs"
                disabled={data.isSubmitting}
                onClick={() => void data.handleApproveAndAssignRecommendation(recommendation.recommendation_id)}
              >
                Approve + assign
              </button>
            </div>
          </div>
        ))}
      </section>

      <section className="training-panel overflow-hidden">
        <div className="border-b border-[color:var(--training-border)] bg-[var(--training-surface-2)] px-4 py-3">
          <p className="training-eyebrow">Competency Queue</p>
          <h2 className="section-title mt-1">Validate demonstrated skills</h2>
        </div>
        {visibleCompetencyValidations.length === 0 ? <div className="px-4 py-4 text-sm text-slate-500">No pending competency validations for this filter.</div> : null}
        {visibleCompetencyValidations.map((validation) => (
          <div key={validation.competency_validation_id} className="border-b border-slate-100 px-4 py-4 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-slate-900">{formatTrainingModuleTitle(validation.training_assignments?.education_modules, validation.assignment_id)}</p>
                <p className="mt-1 text-slate-600">
                  {validation.training_assignments?.education_modules?.learning_depth || 'validation'} · due {validation.training_assignments?.due_date || 'not set'}
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {validation.training_assignments?.education_modules?.module_code ? <Pill tone="gold">{validation.training_assignments.education_modules.module_code}</Pill> : null}
                  {validation.latest_completion?.completed_at ? <Pill tone="blue">completed {validation.latest_completion.completed_at.slice(0, 10)}</Pill> : null}
                  {validation.evidence_package?.package_status ? <Pill tone="gold">evidence {validation.evidence_package.package_status}</Pill> : null}
                </div>
              </div>
              <span className="badge-in-progress">{validation.validation_status}</span>
            </div>
            {(validation.latest_history_entry || validation.history?.length) ? (
              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <p className="font-semibold uppercase tracking-[0.18em] text-slate-500">Validation trail</p>
                <div className="mt-2 space-y-2">
                  {(validation.history || []).slice(0, 3).map((entry) => (
                    <div key={entry.competency_validation_history_id}>
                      <span className="font-semibold text-slate-700">{entry.event_type}</span>
                      <span className="ml-2">{entry.created_at.slice(0, 10)}</span>
                      {entry.event_notes ? <p className="mt-1 text-slate-600">{entry.event_notes}</p> : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <textarea
              value={data.competencyReviewNotes[validation.competency_validation_id] || validation.validation_notes || ''}
              onChange={(event) => data.setCompetencyReviewNote(validation.competency_validation_id, event.target.value)}
              placeholder="Reviewer notes (required to reject, optional to approve)"
              className="training-input mt-3 min-h-[88px]"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" className="btn-primary px-4 py-2 text-xs" disabled={data.isSubmitting} onClick={() => void data.handleReviewCompetencyValidation(validation.competency_validation_id, 'approved')}>
                Approve validation
              </button>
              <button type="button" className="rounded-xl border border-rose-300 px-4 py-2 text-xs font-bold text-rose-700" disabled={data.isSubmitting} onClick={() => void data.handleReviewCompetencyValidation(validation.competency_validation_id, 'rejected')}>
                Reject validation
              </button>
            </div>
          </div>
        ))}
      </section>
    </div>
  )
}

export function LearnerSection({ data }: { data: WorkspaceData }) {
  const { viewerContext } = useAuth()
  const [selectedLessonAssignmentId, setSelectedLessonAssignmentId] = useState<string | null>(null)
  const [lessonPanelIndex, setLessonPanelIndex] = useState(0)
  const [locallyCompletedAssignmentIds, setLocallyCompletedAssignmentIds] = useState<Set<string>>(() => new Set())
  const competencyValidationByAssignment = new Map(
    data.competencyValidations.map((validation) => [validation.assignment_id, validation]),
  )
  const transcriptByAssignment = new Map(
    data.transcript.map((row) => [row.assignment_id, row]),
  )
  const visibleAssignments = data.assignments.filter((assignment) => {
    if (!viewerContext || viewerContext.role === 'Admin' || viewerContext.role === 'Reviewer') return true
    if (assignment.assignee_user_id && assignment.assignee_user_id === viewerContext.user_id) return true
    if (assignment.assignment_scope === 'role' && matchesViewerAssignmentRole(assignment.target_role, viewerContext.role)) return true
    return assignment.assignment_scope === 'facility'
  })
  const selectedLessonAssignment = selectedLessonAssignmentId
    ? visibleAssignments.find((assignment) => assignment.assignment_id === selectedLessonAssignmentId) || null
    : null
  const selectedLessonCompleted = selectedLessonAssignment
    ? Boolean(selectedLessonAssignment.latest_completion || transcriptByAssignment.get(selectedLessonAssignment.assignment_id) || locallyCompletedAssignmentIds.has(selectedLessonAssignment.assignment_id))
    : false
  const lessonPanels = buildTrainingLessonPanels(selectedLessonAssignment)
  const safeLessonPanelIndex = Math.min(lessonPanelIndex, Math.max(lessonPanels.length - 1, 0))
  const activeLessonPanel = selectedLessonAssignment ? lessonPanels[safeLessonPanelIndex] : null
  const lessonProgressPercent = activeLessonPanel && lessonPanels.length > 0
    ? Math.round(((safeLessonPanelIndex + 1) / lessonPanels.length) * 100)
    : 0

  return (
    <div className="space-y-6">
      <section className="training-panel p-5">
        <p className="training-eyebrow">Assigned Learning</p>
        <h1 className="section-title mt-1">Learner workspace</h1>
        <p className="mt-2 text-sm text-slate-600">Use approved quizzes and assignment evidence to close learning loops without leaving the training route tree.</p>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <MetricMini label="Assigned" value={String(data.learnerProgressSummary.assignedCount)} />
          <MetricMini label="Completed" value={String(data.learnerProgressSummary.completedCount)} />
          <MetricMini label="Attested" value={String(data.learnerProgressSummary.attestedCount)} />
          <MetricMini label="Assets logged" value={String(data.learnerProgressSummary.assetCompletionCount)} />
          <MetricMini label="Validation pending" value={String(data.learnerProgressSummary.validationPendingCount)} />
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[0.8fr_1.2fr]">
        <div className="training-panel p-5 space-y-3">
          <h2 className="section-title">My assignment queue</h2>
          {visibleAssignments.map((assignment) => {
            const learningDepth = assignment.education_modules?.learning_depth || null
            const requiresValidation = learningDepth === 'skill_demonstration' || learningDepth === 'validated_competency'
            const validation = competencyValidationByAssignment.get(assignment.assignment_id)
            const transcriptRow = transcriptByAssignment.get(assignment.assignment_id)
            const assetProgress = assignment.learner_asset_progress
            const assets = assignment.education_modules?.education_module_assets || []
            const isCompleted = Boolean(assignment.latest_completion || transcriptRow || locallyCompletedAssignmentIds.has(assignment.assignment_id))
            const contentPreview = cleanTrainingContentPreview(getTrainingContentBlockText(assignment.education_modules?.content_blocks?.[0]))
            return (
              <div key={assignment.assignment_id} className="rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">{formatTrainingModuleTitle(assignment.education_modules, assignment.assignment_id)}</p>
                    <p className="mt-1 text-sm text-slate-600">Due {assignment.due_date}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Pill tone={isCompleted ? 'blue' : 'gold'}>{isCompleted ? 'completed' : assignment.assignment_state || 'assigned'}</Pill>
                    {assignment.education_modules?.module_code ? <Pill>{assignment.education_modules.module_code}</Pill> : null}
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <InfoLine label="Duration" value={assignment.education_modules?.duration_minutes ? `${assignment.education_modules.duration_minutes} min` : 'Not set'} />
                  <InfoLine label="Tracks" value={assignment.education_modules?.related_track_ids?.join(', ') || 'None'} />
                  <InfoLine label="Policy refs" value={assignment.education_modules?.linked_policy_refs?.join(', ') || 'None'} />
                  <InfoLine label="Asset events" value={`${assetProgress?.completed_count || 0} completed / ${assetProgress?.started_count || 0} started`} />
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn-primary px-4 py-2 text-xs"
                    onClick={() => {
                      setSelectedLessonAssignmentId(assignment.assignment_id)
                      setLessonPanelIndex(0)
                    }}
                  >
                    {selectedLessonAssignmentId === assignment.assignment_id ? 'Lesson open' : 'Open lesson'}
                  </button>
                </div>
                {contentPreview ? (
                  <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-600">
                    {contentPreview}
                  </div>
                ) : null}
                <div className="mt-4">
                  <p className="training-eyebrow">Related assets</p>
                  <div className="mt-2 space-y-2">
                    {assets.length > 0 ? assets.map((asset) => (
                      <div key={asset.asset_id} className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="font-semibold capitalize text-slate-900">{asset.kind}</p>
                            <p className="mt-1 text-xs text-slate-500">{asset.storage_mode} · {asset.mime_type}</p>
                          </div>
                          <Pill tone={asset.status === 'approved' ? 'blue' : 'gold'}>{asset.status}</Pill>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" className="btn-ghost px-3 py-2 text-xs" disabled={data.isSubmitting} onClick={() => void data.handleRecordAssetProgress(assignment.module_id, 'view_started')}>
                            Log start
                          </button>
                          <button type="button" className="btn-ghost px-3 py-2 text-xs" disabled={data.isSubmitting} onClick={() => void data.handleRecordAssetProgress(assignment.module_id, 'view_completed')}>
                            Log completion
                          </button>
                        </div>
                      </div>
                    )) : <EmptyCard message="No related assets are attached to this assignment yet." />}
                  </div>
                </div>
                {requiresValidation ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {validation ? <Pill tone={validation.validation_status === 'approved' ? 'blue' : validation.validation_status === 'rejected' ? 'rose' : 'gold'}>{validation.validation_status}</Pill> : null}
                    {!validation || validation.validation_status === 'rejected' ? (
                      <button type="button" className="btn-ghost px-3 py-2 text-xs" disabled={data.isSubmitting} onClick={() => void data.handleRequestCompetencyValidation(assignment.assignment_id)}>
                        Request validation
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3">
                  <label className="flex items-start gap-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(data.learnerAttestations[assignment.assignment_id])}
                      onChange={(event) => data.setLearnerAttestation(assignment.assignment_id, event.target.checked)}
                    />
                    <span>I attest that I completed the assigned learning materials and reviewed the required content for this module.</span>
                  </label>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="btn-primary px-4 py-2 text-xs"
                      onClick={() => {
                        setLocallyCompletedAssignmentIds((current) => {
                          const next = new Set(current)
                          next.add(assignment.assignment_id)
                          return next
                        })
                        void data.handleMarkCompletion(assignment.assignment_id)
                      }}
                      disabled={data.isSubmitting || !data.learnerAttestations[assignment.assignment_id] || isCompleted}
                    >
                      {isCompleted ? 'Completion recorded' : 'Record attestation'}
                    </button>
                    <span className="text-xs text-slate-500">
                      {transcriptRow?.completed_at
                        ? `Completed ${new Date(transcriptRow.completed_at).toLocaleDateString()}`
                        : assignment.latest_completion?.completed_at
                          ? `Completed ${new Date(assignment.latest_completion.completed_at).toLocaleDateString()}`
                          : 'Completion not yet recorded'}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
          {visibleAssignments.length === 0 ? <EmptyCard message="No learner assignments are currently open." /> : null}
        </div>

        <div className="space-y-4">
          <div className="training-panel p-5 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="training-eyebrow">Lesson</p>
                <h2 className="section-title">Lesson viewer</h2>
              </div>
              {activeLessonPanel ? <Pill tone={activeLessonPanel.kind === 'asset' ? 'gold' : 'blue'}>{activeLessonPanel.kind}</Pill> : null}
            </div>
            {selectedLessonAssignment && activeLessonPanel ? (
              <div className="rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                      Section {safeLessonPanelIndex + 1} of {lessonPanels.length}
                    </p>
                    <h3 className="mt-2 text-lg font-black text-slate-900">{formatTrainingModuleTitle(selectedLessonAssignment.education_modules, selectedLessonAssignment.assignment_id)}</h3>
                    <p className="mt-1 text-sm text-slate-600">Due {selectedLessonAssignment.due_date}</p>
                  </div>
                  <Pill tone="blue">{lessonProgressPercent}% viewed</Pill>
                </div>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-white">
                  <div className="h-full rounded-full bg-[var(--brand-teal)]" style={{ width: `${lessonProgressPercent}%` }} />
                </div>
                <article className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="training-eyebrow">{activeLessonPanel.kind === 'asset' ? 'Media' : 'Lesson section'}</p>
                  <h4 className="mt-2 text-base font-black text-slate-900">{activeLessonPanel.title}</h4>
                  <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-700">{activeLessonPanel.body}</p>
                  {activeLessonPanel.meta ? <p className="mt-3 text-xs font-semibold text-slate-500">{activeLessonPanel.meta}</p> : null}
                  {activeLessonPanel.kind === 'asset' && activeLessonPanel.asset ? (
                    <AssetMediaPlayer asset={activeLessonPanel.asset} />
                  ) : null}
                </article>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      aria-label="Previous lesson panel"
                      className="btn-ghost px-4 py-2 text-sm"
                      disabled={safeLessonPanelIndex === 0}
                      onClick={() => setLessonPanelIndex((index) => Math.max(0, index - 1))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      aria-label="Next lesson panel"
                      className="btn-ghost px-4 py-2 text-sm"
                      disabled={safeLessonPanelIndex >= lessonPanels.length - 1}
                      onClick={() => setLessonPanelIndex((index) => Math.min(lessonPanels.length - 1, index + 1))}
                    >
                      Next
                    </button>
                  </div>
                  <span className="text-xs text-slate-500">Attestation remains required before transcript completion.</span>
                </div>
              </div>
            ) : (
              <EmptyCard message="Choose an assignment card to open the lesson." />
            )}
          </div>

          {selectedLessonAssignment && !selectedLessonCompleted ? (
            <div className="training-panel p-5">
              <p className="training-eyebrow">Quiz</p>
              <EmptyCard message="Complete the lesson and record attestation to unlock the quiz." />
            </div>
          ) : null}

          {selectedLessonAssignment && selectedLessonCompleted ? (
          <div className="training-panel p-5 space-y-4">
            <h2 className="section-title">Quiz</h2>
            <select value={data.selectedQuizId} onChange={(event) => data.setSelectedQuizId(event.target.value)} className="training-input" disabled={data.approvedQuizzes.length === 0}>
              <option value="">Select approved quiz</option>
              {data.approvedQuizzes.map((quiz) => <option key={quiz.quiz_id} value={quiz.quiz_id}>{formatTrainingModuleTitle(quiz.education_modules, quiz.module_id)}</option>)}
            </select>
            {data.approvedQuizzes.length === 0 ? <EmptyCard message="No approved quizzes yet. Reviewer approval is required before learner launch." /> : null}
            {data.quizLoadError ? <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{data.quizLoadError}</div> : null}
            {data.quizPlayerData ? (
              <div className="space-y-4 rounded-2xl border border-[color:var(--training-border)] bg-white p-4">
                <p className="training-eyebrow">Question {data.quizQuestionIndex + 1} of {data.quizPlayerData.questions.length}</p>
                <p className="text-base font-semibold text-slate-900">{data.currentQuizQuestion?.question_text}</p>
                <div className="space-y-2">
                  {data.currentQuizQuestion?.answer_options.map((option, optionIndex) => (
                    <label key={optionIndex} className="flex items-start gap-2 rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-700">
                      <input type="radio" name={`quiz-question-${data.quizQuestionIndex}`} checked={data.quizAnswers[data.quizQuestionIndex] === optionIndex} onChange={() => data.setAnswer(data.quizQuestionIndex, optionIndex)} />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-ghost px-4 py-2 text-sm" disabled={data.quizQuestionIndex === 0} onClick={() => data.setQuizQuestionIndex((index) => Math.max(0, index - 1))}>Previous</button>
                  <button type="button" className="btn-ghost px-4 py-2 text-sm" disabled={data.quizQuestionIndex >= data.quizPlayerData.questions.length - 1} onClick={() => data.setQuizQuestionIndex((index) => Math.min(data.quizPlayerData!.questions.length - 1, index + 1))}>Next</button>
                  <button type="button" className="btn-primary px-4 py-2 text-sm" disabled={data.isSubmitting} onClick={() => void data.handleSubmitQuiz()}>Submit attempt</button>
                </div>
                {data.lastAttemptResult ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    {data.lastAttemptResult.score_percent}% ({data.lastAttemptResult.correct_count}/{data.lastAttemptResult.question_count}) - {data.lastAttemptResult.passed ? 'Passed' : 'Failed'}
                  </div>
                ) : null}
              </div>
            ) : null}
            {data.attemptHistory.length > 0 ? (
              <div className="rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-4">
                <p className="training-eyebrow">Recent attempts</p>
                <div className="mt-3 space-y-2">
                  {data.attemptHistory.map((attempt) => (
                    <div key={`${attempt.quiz_id}-${attempt.attempted_at}`} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-700">{attempt.quiz_label}</span>
                      <span className="font-semibold text-slate-900">{attempt.score_percent}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-4">
              <p className="training-eyebrow">Transcript visibility</p>
              <div className="mt-3 space-y-2">
                {data.transcript.slice(0, 5).map((row) => (
                  <div key={row.completion_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                    <span className="text-slate-700">{formatTrainingModuleTitle(row.training_assignments?.education_modules, row.assignment_id)}</span>
                    <span className="font-semibold text-slate-900">{row.attestation_flag ? 'attested' : 'completed'}</span>
                  </div>
                ))}
                {data.transcript.length === 0 ? <EmptyCard message="Transcript entries will appear here after quiz or attestation completion." /> : null}
              </div>
            </div>
          </div>
          ) : null}
        </div>
      </section>
    </div>
  )
}

function formatTranscriptLearner(userId: string | null | undefined, users: WorkspaceData['assignmentUserOptions']): { name: string; email: string | null } {
  if (!userId) return { name: 'Learner not recorded', email: null }
  const learner = users.find((user) => user.user_id === userId)
  if (!learner) return { name: 'Learner name unavailable', email: null }
  const displayName = [learner.first_name, learner.last_name].filter(Boolean).join(' ').trim()
  return {
    name: displayName || learner.email,
    email: displayName ? learner.email : null,
  }
}

export function TranscriptSection({ data }: { data: WorkspaceData }) {
  const competencyValidationByAssignment = new Map(
    data.competencyValidations.map((validation) => [validation.assignment_id, validation]),
  )

  return (
    <div className="space-y-6">
      <section className="training-panel p-5 space-y-4">
        <div>
          <p className="training-eyebrow">Evidence</p>
          <h1 className="section-title mt-1">Transcript and export workspace</h1>
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          <select aria-label="Filter transcript status" value={data.transcriptStatusFilter} onChange={(event) => data.setTranscriptStatusFilter(event.target.value as 'completed' | 'open' | 'overdue')} className="training-input">
            <option value="completed">All completed</option>
            <option value="open">Open (not expired)</option>
            <option value="overdue">Overdue (expired)</option>
          </select>
          <select aria-label="Filter transcript modules" value={data.transcriptModuleFilter} onChange={(event) => data.setTranscriptModuleFilter(event.target.value)} className="training-input">
            <option value="">All modules</option>
            {data.transcriptModuleOptions.map((moduleName) => <option key={moduleName} value={moduleName}>{moduleName}</option>)}
          </select>
          <select aria-label="Filter transcript tracks" value={data.transcriptTrackFilter} onChange={(event) => data.setTranscriptTrackFilter(event.target.value)} className="training-input">
            <option value="">All tracks</option>
            {data.transcriptTrackOptions.map((track) => <option key={track.value} value={track.value}>{track.label}</option>)}
          </select>
          <select aria-label="Filter transcript deployments" value={data.transcriptDeploymentFilter} onChange={(event) => data.setTranscriptDeploymentFilter(event.target.value)} className="training-input">
            <option value="">All deployments</option>
            {data.transcriptDeploymentOptions.map((deployment) => <option key={deployment.value} value={deployment.value}>{deployment.label}</option>)}
          </select>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-ghost px-4 py-2 text-sm" disabled={data.isSubmitting} onClick={() => void data.handleExport(true)}>Export redacted</button>
            <button type="button" className="btn-primary px-4 py-2 text-sm" disabled={data.isSubmitting} onClick={() => void data.handleExport(false)}>Request unredacted</button>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <MetricMini label="Assignments in scope" value={String(data.transcriptRollup.assignmentCount)} />
          <MetricMini label="Completions" value={String(data.transcriptRollup.completionCount)} />
          <MetricMini label="Evidence packages" value={String(data.transcriptRollup.evidenceCount)} />
          <MetricMini label="CAPA linked" value={String(data.transcriptRollup.capaLinkedCount)} />
          <MetricMini label="Recommendations linked" value={String(data.transcriptRollup.recommendationLinkedCount)} />
          <MetricMini label="Completion rate" value={`${data.transcriptRollup.completionRate}%`} />
        </div>
      </section>

      <section className="training-panel overflow-hidden">
        <div className="border-b border-[color:var(--training-border)] bg-[var(--training-surface-2)] px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Completion records</p>
        </div>
        {data.filteredTranscript.length === 0 ? <div className="px-4 py-4 text-sm text-slate-500">No transcript rows match the selected filters.</div> : null}
        {data.filteredTranscript.map((row) => {
          const expired = isExpired(row.expires_at)
          const validation = competencyValidationByAssignment.get(row.assignment_id)
          const learner = formatTranscriptLearner(row.user_id || validation?.user_id || row.training_assignments?.assignee_user_id, data.assignmentUserOptions)
          const moduleTitle = formatTrainingModuleTitle(row.training_assignments?.education_modules, row.assignment_id)
          const hasSupportContext = Boolean(row.deployment_context?.track_title || row.deployment_context?.assignment_count)
          return (
            <article key={row.completion_id} aria-label={`${learner.name} ${moduleTitle}`} className="border-b border-slate-100 px-4 py-4 text-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <p className="training-eyebrow">Learner</p>
                    <h2 className="mt-1 break-words text-base font-bold text-slate-900">{learner.name}</h2>
                    {learner.email ? <p className="mt-1 break-all text-xs text-slate-500">{learner.email}</p> : null}
                  </div>
                  <div>
                    <p className="break-words font-semibold text-slate-700">{moduleTitle}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {row.training_assignments?.education_modules?.module_code ? <Pill tone="gold">{row.training_assignments.education_modules.module_code}</Pill> : null}
                      {validation ? <Pill tone={validation.validation_status === 'approved' ? 'blue' : validation.validation_status === 'rejected' ? 'rose' : 'gold'}>{validation.validation_status}</Pill> : null}
                      {row.training_assignments?.linked_capa_id || row.recommendation_link?.linked_capa_id ? <Pill tone="rose">CAPA linked</Pill> : null}
                      {row.recommendation_link?.recommendation_id ? <Pill tone="blue">Recommendation linked</Pill> : null}
                    </div>
                  </div>
                  {hasSupportContext ? (
                    <details className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <summary className="cursor-pointer font-semibold text-slate-700">Support context</summary>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {row.deployment_context?.track_title ? <span className="break-words">Track: {row.deployment_context.track_title}</span> : null}
                        {row.deployment_context?.assignment_count ? <span>{row.deployment_context.assignment_count} assignments issued</span> : null}
                      </div>
                    </details>
                  ) : null}
                  {validation?.latest_history_entry?.event_notes ? (
                    <p className="text-xs text-slate-500">Validation note: {validation.latest_history_entry.event_notes}</p>
                  ) : null}
                </div>
                <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[24rem] lg:grid-cols-1">
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Completed</p>
                    <p className="mt-1 font-semibold text-slate-700">{toDateInput(row.completed_at)}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Expires</p>
                    <p className="mt-1 font-semibold text-slate-700">{toDateInput(row.expires_at)}</p>
                    <p className="mt-1 text-[11px] text-slate-400">{row.evidence_package_id ? `Evidence ${row.evidence_package_id.slice(0, 8)}` : 'No package yet'}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Status</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={expired ? 'badge-archived' : 'badge-released'}>{expired ? 'expired' : 'active'}</span>
                      <button type="button" className="btn-ghost px-3 py-2 text-[11px]" disabled={data.isSubmitting} onClick={() => void data.handleGenerateEvidencePackage(row.completion_id, true)}>
                        Generate evidence
                      </button>
                      <button
                        type="button"
                        className="btn-ghost px-3 py-2 text-[11px]"
                        disabled={data.isEvidenceLoading || !row.evidence_package_id}
                        onClick={() => void data.handleLoadEvidencePackage(row.completion_id)}
                      >
                        View package
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          )
        })}
      </section>

      {data.selectedEvidencePackage ? (
        <section className="training-panel p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="training-eyebrow">Evidence Package</p>
              <h2 className="section-title mt-1">{data.selectedEvidencePackage.evidence_package_id}</h2>
            </div>
            <button type="button" className="btn-ghost px-4 py-2 text-sm" onClick={data.clearEvidenceSelection}>
              Close
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <MetricMini label="Status" value={data.selectedEvidencePackage.package_status} />
            <MetricMini label="Redacted" value={data.selectedEvidencePackage.redacted ? 'Yes' : 'No'} />
            <MetricMini label="Assignment" value={data.selectedEvidencePackage.assignment_id.slice(0, 8)} />
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <MetricMini label="Track" value={String((data.selectedEvidencePackage.payload as any)?.deployment_context?.track_title || (data.selectedEvidencePackage.payload as any)?.deployment_context?.track_key || 'Unscoped')} />
            <MetricMini label="Deployment" value={String((data.selectedEvidencePackage.payload as any)?.deployment_context?.deployment_id || 'None').slice(0, 8)} />
            <MetricMini label="Remediation" value={String((data.selectedEvidencePackage.payload as any)?.recommendation_link?.remediation_strength || (data.selectedEvidencePackage.payload as any)?.assignment?.remediation_strength || 'training_only').replaceAll('_', ' ')} />
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <MetricMini label="Validation" value={String((data.selectedEvidencePackage.payload as any)?.competency_validation?.validation_status || 'none')} />
            <MetricMini label="Latest note" value={String((data.selectedEvidencePackage.payload as any)?.competency_validation?.validation_notes || (data.selectedEvidencePackage.payload as any)?.competency_validation?.history?.[0]?.event_notes || 'None')} />
            <MetricMini label="History events" value={String(((data.selectedEvidencePackage.payload as any)?.competency_validation?.history || []).length)} />
          </div>
          <div className="mt-4 rounded-2xl border border-[color:var(--training-border)] bg-[var(--training-surface-2)] p-4">
            <p className="training-eyebrow">Payload</p>
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap text-xs text-slate-700">
              {JSON.stringify(data.selectedEvidencePackage.payload, null, 2)}
            </pre>
          </div>
        </section>
      ) : null}
    </div>
  )
}
